import type { Locale } from '@/i18n/translations';
import { restoreDialogFocus } from '@/components/ui/focus';
import { DonorError } from './http';
import { verificationCopy } from './verification-copy';
interface Turnstile {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}
let loading: Promise<void> | undefined;
const pendingVerifications = new Set<() => void>();

/** A proof must not resume an action after its page or account has changed. */
export function cancelBrowserVerification(): void {
  for (const cancel of pendingVerifications) cancel();
}
function load(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    const timer = setTimeout(() => {
      loading = undefined;
      script.remove();
      reject(new DonorError(0, 'BROWSER_VERIFICATION_UNAVAILABLE'));
    }, 15000);
    script.onload = () => {
      clearTimeout(timer);
      if (window.turnstile) resolve();
      else {
        loading = undefined;
        script.remove();
        reject(new DonorError(0, 'BROWSER_VERIFICATION_UNAVAILABLE'));
      }
    };
    script.onerror = () => {
      clearTimeout(timer);
      loading = undefined;
      script.remove();
      reject(new DonorError(0, 'BROWSER_VERIFICATION_UNAVAILABLE'));
    };
    document.head.append(script);
  });
  return loading;
}
export async function verifyHuman(
  siteKey: string | null,
  action: string,
  locale: Locale,
): Promise<string | undefined> {
  // The API omits the site key when Turnstile is explicitly disabled. The
  // server still enforces its origin, session and rate-limit requirements.
  if (siteKey === null) return undefined;
  if (!siteKey) throw new DonorError(503, 'WEB_CHECKOUT_DISABLED');
  return new Promise<string>((resolve, reject) => {
    let dialog: HTMLDialogElement | undefined;
    let widget: string | undefined;
    let api: Turnstile | undefined;
    let opener: HTMLElement | null = null;
    let interactive = false;
    let finished = false;
    const removeWidget = (): void => {
      if (!widget) return;
      const id = widget;
      widget = undefined;
      try {
        api?.remove(id);
      } catch {
        // Removing the host still cleans up an iframe if the provider failed.
      }
    };
    const timer = setTimeout(() => finish(), 120000);
    const cancel = (): void => finish();
    const finish = (
      token?: string,
      failure: unknown = new DonorError(0, 'BROWSER_VERIFICATION_REQUIRED'),
    ): void => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      pendingVerifications.delete(cancel);
      removeWidget();
      dialog?.close();
      dialog?.remove();
      if (interactive) restoreDialogFocus(opener, dialog);
      if (token) resolve(token);
      else
        reject(
          failure instanceof Error ? failure : new DonorError(0, 'BROWSER_VERIFICATION_REQUIRED'),
        );
    };
    pendingVerifications.add(cancel);
    void load().then(
      () => {
        if (finished) return;
        try {
          api = window.turnstile;
          if (!api) {
            finish();
            return;
          }
          const copy = verificationCopy[locale];
          const id = `verification-${crypto.randomUUID()}`;
          dialog = document.createElement('dialog');
          dialog.className = 'giving-verification giving-verification--background';
          dialog.setAttribute('aria-hidden', 'true');
          dialog.setAttribute('aria-labelledby', `${id}-title`);
          dialog.setAttribute('aria-describedby', `${id}-description`);
          const header = document.createElement('header');
          header.className = 'giving-verification__header';
          const title = document.createElement('h2');
          title.id = `${id}-title`;
          title.tabIndex = -1;
          title.setAttribute('autofocus', '');
          title.textContent = copy.title;
          const description = document.createElement('p');
          description.id = `${id}-description`;
          description.textContent = copy.description;
          header.append(title, description);
          const container = document.createElement('div');
          container.className = 'giving-verification__widget';
          const footer = document.createElement('footer');
          footer.className = 'giving-verification__footer';
          const close = document.createElement('button');
          close.type = 'button';
          close.className = 'ui-button ui-button--secondary';
          close.textContent = copy.close;
          footer.append(close);
          dialog.append(header, container, footer);
          // Setting open directly keeps the widget rendered without showing a modal,
          // moving its iframe, stealing focus or interrupting the current page.
          dialog.open = true;
          document.body.append(dialog);
          dialog.addEventListener('cancel', (event) => {
            event.preventDefault();
            event.stopPropagation();
            finish();
          });
          close.addEventListener('click', cancel);
          widget = api.render(container, {
            sitekey: siteKey,
            action,
            language: locale,
            theme: document.documentElement.dataset['theme'] === 'dark' ? 'dark' : 'light',
            size: 'flexible',
            appearance: 'interaction-only',
            'before-interactive-callback': () => {
              if (finished || interactive || !dialog) return;
              opener =
                document.activeElement instanceof HTMLElement ? document.activeElement : null;
              interactive = true;
              try {
                dialog.close();
                dialog.classList.remove('giving-verification--background');
                dialog.removeAttribute('aria-hidden');
                dialog.showModal();
              } catch {
                finish();
              }
            },
            callback: (token: string) => finish(token),
            'error-callback': cancel,
            'expired-callback': cancel,
            'timeout-callback': cancel,
            'unsupported-callback': cancel,
          });
          // Provider callbacks may also run synchronously during rendering.
          if (finished) removeWidget();
        } catch {
          finish();
        }
      },
      (failure: unknown) => finish(undefined, failure),
    );
  });
}
